import React from 'react'
import { markShown, shownRecently } from '../utils'

interface priorityContext {
  register: (element: { id: string; priority: number }) => void
  unregister: (element: { id: string }) => void
  activeID: string | undefined
}

const PriorityContext = React.createContext<priorityContext>({} as priorityContext)

/**
 * PriorityProvider is a context provider that allows components to register themselves with a priority.
 * The component with the highest priority will be the active one.
 *
 * It it used to avoid multiple modals being shown at the same time, and a few other things.
 */
export const PriorityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const elements = React.useRef<{ id: string; priority: number }[]>([])

  // we need to use a ref and a state, so that the stable functions register and unregister can view the value (through the ref)
  // and also the component can re-render when the value changes
  const activeIDRef = React.useRef<string | undefined>(undefined)
  const [activeID, setActiveIDRaw] = React.useState<string | undefined>(undefined)
  const setActiveID = React.useCallback((id: string | undefined) => {
    activeIDRef.current = id
    setActiveIDRaw(id)
  }, [])

  const getMaxPriorityElement = React.useCallback(
    () => elements.current.find((el) => el.priority === Math.max(-9999, ...elements.current.map((el) => el.priority))),
    []
  )

  const register = React.useCallback((element: { id: string; priority: number }) => {
    if (elements.current.some((el) => el.id === element.id)) return
    elements.current.push(element)
    if (element === getMaxPriorityElement()) setActiveID(element.id)
  }, [])

  const unregister = React.useCallback((element: { id: string }) => {
    elements.current = elements.current.filter((el) => el.id !== element.id)
    if (activeIDRef.current === element.id) setActiveID(getMaxPriorityElement()?.id)
  }, [])

  return (
    <PriorityContext.Provider
      value={{
        register,
        unregister,
        activeID,
      }}
    >
      {children}
    </PriorityContext.Provider>
  )
}

/**
 * usePriorityEventContext is a hook that returns the current active ID and the register and unregister functions.
 * It is used to register, watch, and unregister events with a priority.
 */
export const usePriorityEventContext = () => {
  const { activeID, register, unregister } = React.useContext(PriorityContext)
  return { activeID, register, unregister }
}

/**
 * useShowWithPriority is a hook that allows you to register a component with a priority.
 * Mark when this event is complete by calling the onComplete function. If this component
 * unmounts, it is automatically marked complete.
 *
 * @param priority - The priority of the component. Higher numbers are shown first.
 * @returns The active state and an onComplete function to unregister the component when it is done.
 */
export const useShowWithPriority = (priority: number) => {
  const { activeID, register, unregister } = usePriorityEventContext()
  const id = React.useId()
  const onComplete = () => unregister({ id })
  React.useEffect(() => {
    register({ id, priority })
    return () => unregister({ id })
  }, [id])
  return { active: id === activeID, onComplete }
}

/**
 * useShowWithPriorityIfNotShownRecently is a hook that allows you to register a component with a priority,
 * but only if it has not been shown recently. If it has been shown recently, it will not register the component.
 * Shown recently is just checked on first mount.
 *
 * @param priority - The priority of the component. Higher numbers are shown first.
 * @param key - The key used to check if the component has been shown recently.
 * @param minutes - The number of minutes to check for recent shows. Default is 60 minutes.
 * @returns The active state and an onComplete function to unregister the component when it is done.
 */
export const useShowWithPriorityIfNotShownRecently = (priority: number, key: string, minutes: number = 60) => {
  const { activeID, register, unregister } = usePriorityEventContext()
  const id = React.useId()
  const onComplete = () => {
    unregister({ id })
    markShown(key)
  }
  const tooSoonToShow = React.useMemo(() => shownRecently(key, minutes), [])
  React.useEffect(() => {
    if (tooSoonToShow) return
    register({ id, priority })
    return () => unregister({ id })
  }, [id])
  return { active: id === activeID, onComplete }
}
